import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AsyncSelect from 'react-select/async';
import { fetchOrganizationById, fetchOrganizations } from '../../services/organizationService';

type OrgOption = { value: string; label: string };

// Light theme — matches prompt-config tokens ($pc-primary: #005581, $pc-primary-light: #e0eaff)
export const lightStyles = {
  control: (base: any, state: any) => ({
    ...base,
    backgroundColor: '#ffffff',
    borderColor: state.isFocused ? '#005581' : '#e5e7eb',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(0, 85, 129, 0.15)' : 'none',
    borderRadius: '0.375rem',
    minHeight: '36px',
    fontSize: '0.875rem',
    '&:hover': { borderColor: '#d1d5db' },
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected ? '#005581' : state.isFocused ? '#e0eaff' : '#ffffff',
    color: state.isSelected ? '#ffffff' : '#111827',
    fontSize: '0.875rem',
    '&:active': { backgroundColor: '#005581', color: '#ffffff' },
  }),
  placeholder: (base: any) => ({ ...base, color: '#6b7280', fontSize: '0.875rem' }),
  singleValue: (base: any) => ({ ...base, color: '#111827', fontSize: '0.875rem' }),
  menu: (base: any) => ({ ...base, zIndex: 9999, borderRadius: '0.375rem', border: '1px solid #e5e7eb' }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  menuList: (base: any) => ({ ...base, padding: '4px' }),
  multiValue: (base: any) => ({ ...base, backgroundColor: '#e0eaff', borderRadius: '0.25rem' }),
  multiValueLabel: (base: any) => ({ ...base, color: '#005581', fontSize: '0.875rem' }),
  multiValueRemove: (base: any) => ({
    ...base,
    color: '#005581',
    '&:hover': { backgroundColor: '#005581', color: '#ffffff' },
  }),
};

// Dark theme — matches scene editor admin bar (scene-select class + toolbar blue accent #60a5fa)
const darkStyles = {
  control: (base: any, state: any) => ({
    ...base,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderColor: state.isFocused ? '#60a5fa' : 'rgba(255, 255, 255, 0.2)',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(96, 165, 250, 0.25)' : 'none',
    borderRadius: '4px',
    minHeight: '28px',
    fontSize: '12px',
    '&:hover': { borderColor: 'rgba(255, 255, 255, 0.4)' },
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected ? '#2563eb' : state.isFocused ? 'rgba(96, 165, 250, 0.15)' : 'transparent',
    color: '#e2e8f0',
    fontSize: '12px',
    '&:active': { backgroundColor: '#2563eb' },
  }),
  placeholder: (base: any) => ({ ...base, color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px' }),
  singleValue: (base: any) => ({ ...base, color: '#ffffff', fontSize: '12px' }),
  input: (base: any) => ({ ...base, color: '#ffffff' }),
  menu: (base: any) => ({ ...base, zIndex: 9999, backgroundColor: '#1e293b', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '4px' }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  menuList: (base: any) => ({ ...base, padding: '4px', backgroundColor: '#1e293b' }),
  dropdownIndicator: (base: any) => ({ ...base, color: 'rgba(255,255,255,0.6)', padding: '0 4px' }),
  clearIndicator: (base: any) => ({ ...base, color: 'rgba(255,255,255,0.6)' }),
  indicatorSeparator: () => ({ display: 'none' }),
};

type Props = {
  value: string | null;
  // `label` is the selected option's display name (or `null` for a cleared/platform-wide
  // selection) — pass it as a second param instead of a separate `onLabel` callback so
  // callers get both pieces of a single selection event atomically.
  onChange: (id: string | null, label: string | null) => void;
  disabled?: boolean;
  inputId?: string;
  variant?: 'dark';
  // Whether the "no org" option (value `null`) is offered at all. Set to `false` for
  // required org fields where "platform-wide" isn't a valid selection.
  allowPlatformWide?: boolean;
  // Label for the "no org" option. Defaults to the platform-wide copy; pass something like
  // "All organizations" when this is used as an "everything" filter rather than a scope picker.
  emptyOptionLabel?: string;
};

const OrgSelect: React.FC<Props> = ({
  value,
  onChange,
  disabled,
  inputId,
  variant,
  allowPlatformWide = true,
  emptyOptionLabel: emptyOptionLabelProp,
}) => {
  const { t } = useTranslation();
  const emptyOptionLabel = emptyOptionLabelProp ?? t('orgSelect.platformWide');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The previous call's `resolve`, if a newer keystroke supersedes it before its timeout
  // fires — cancelling the timeout alone leaves that Promise (already handed to
  // react-select) permanently pending, which can strand the control in a loading state.
  const pendingResolveRef = useRef<((options: OrgOption[]) => void) | null>(null);
  const [resolvedLabel, setResolvedLabel] = useState<string | null>(null);
  const [isResolving, setIsResolving] = useState(false);
  // Declared inline (not via useMemo) since it's only used for its `label` field below —
  // see loadOptions, which builds its own copy rather than depending on this reference.
  const emptyOption: OrgOption = { value: '', label: emptyOptionLabel };

  // When value is pre-set (e.g. coming from context selection), resolve the org name
  useEffect(() => {
    if (!value) {
      setResolvedLabel(null);
      setIsResolving(false);
      return;
    }
    let cancelled = false;
    setIsResolving(true);
    setResolvedLabel(null);
    fetchOrganizationById(value)
      .then(org => { if (!cancelled) { setResolvedLabel(org.name); setIsResolving(false); } })
      .catch(() => { if (!cancelled) { setResolvedLabel(value); setIsResolving(false); } });
    return () => { cancelled = true; };
  }, [value]);

  const loadOptions = useCallback(
    (inputValue: string): Promise<OrgOption[]> =>
      new Promise(resolve => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        pendingResolveRef.current?.([]);
        pendingResolveRef.current = resolve;
        debounceRef.current = setTimeout(async () => {
          pendingResolveRef.current = null;
          // The "no org" option carries no label of its own: formatOptionLabel words it at
          // render, so it follows the UI language rather than the one active when the
          // options were fetched (cacheOptions/defaultOptions keep them for the mount).
          const prefix = allowPlatformWide ? [{ value: '', label: '' }] : [];
          try {
            const result = await fetchOrganizations(1, 20, inputValue);
            resolve([...prefix, ...result.items.filter(o => o.id).map(o => ({ value: o.id as string, label: o.name }))]);
          } catch {
            resolve(prefix);
          }
        }, 300);
      }),
    [allowPlatformWide],
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      pendingResolveRef.current?.([]);
    };
  }, []);

  const selectedOption: OrgOption | null = value
    ? { value, label: isResolving ? '...' : (resolvedLabel ?? value) }
    : allowPlatformWide ? emptyOption : null;

  return (
    <AsyncSelect<OrgOption>
      inputId={inputId}
      cacheOptions
      defaultOptions
      loadOptions={loadOptions}
      formatOptionLabel={opt => (opt.value === '' ? emptyOptionLabel : opt.label)}
      value={selectedOption}
      onChange={opt => {
        const id = opt?.value || null;
        const label = opt?.value ? (opt.label ?? null) : null;
        setResolvedLabel(label);
        onChange(id, label);
      }}
      isDisabled={disabled}
      styles={variant === 'dark' ? darkStyles : lightStyles}
      placeholder={t('orgSelect.searchPlaceholder')}
      loadingMessage={() => t('orgSelect.loading')}
      noOptionsMessage={() => t('orgSelect.noOptions')}
      // Modals clip overflow (e.g. Add User), so the menu has to escape its container.
      menuPortalTarget={document.body}
      menuPosition="fixed"
    />
  );
};

export default OrgSelect;
